import React from "react";
import PropTypes from "prop-types";
import A_TextField from "../../../Atoms/Inputs/TextFields/A_TextField";
import A_DatePicker from "../../../Atoms/Inputs/DateTimePickers/A_DatePicker";
import { formatPercent } from "../../../../../domain/provision/provision.rules";
import { toIsoDate } from "../../../../../domain/provision/provision.rules";
import { MONEY_DECIMAL_PLACES } from "../../../../../domain/provision/provision.types";
import useStyles from "./Provision.styles";

/**
 * Provision Details (claude-code-UI-prompt.md §Screen structure 1, DFXX2-8): Provision Amount,
 * Approval Date, GCE Net of Provision, and the four auto-calculated read-only ratios. Ratios
 * come straight from `provision.details` (server-computed) — they are never derived client-side
 * and never posted back on save.
 */
const ProvisionDetailsSection = ({ details, draft, onChange, editable }) => {
  const classes = useStyles();

  const setField = (field) => (event) => onChange({ ...draft, [field]: event.target.value });

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <span className={classes.cardHeaderTitle}>Provision Details</span>
      </div>
      <div className={classes.cardBody}>
        <p className={classes.subtle} style={{ marginTop: 0 }}>
          Record and maintain the current provision position for this loan.
        </p>

        <div className={classes.frow}>
          <A_TextField
            id="provisionAmount"
            label="Provision Amount"
            type="number"
            adornment={{ position: "start", value: "£" }}
            defaultValue={draft.provisionAmount ?? ""}
            onChange={setField("provisionAmount")}
            disabled={!editable}
            inputMaxDecimals={MONEY_DECIMAL_PLACES}
            inputProps={{ step: "0.01", min: "0" }}
            variant="outlined"
            size="small"
            margin="dense"
            fullWidth
          />
          <A_DatePicker
            id="provisionApprovalDate"
            label="Provision Approval Date"
            margin="dense"
            defaultValue={draft.provisionApprovalDate}
            onChange={(value) => onChange({ ...draft, provisionApprovalDate: toIsoDate(new Date(value * 1000)) })}
            disabled={!editable}
            fullWidth
          />
        </div>

        <div className={classes.frow}>
          <A_TextField
            id="gceNetOfProvision"
            label="GCE Net of Provision"
            type="number"
            adornment={{ position: "start", value: "£" }}
            defaultValue={draft.gceNetOfProvision ?? ""}
            onChange={setField("gceNetOfProvision")}
            disabled={!editable}
            inputMaxDecimals={MONEY_DECIMAL_PLACES}
            inputProps={{ step: "0.01", min: "0" }}
            variant="outlined"
            size="small"
            margin="dense"
            fullWidth
          />
          <A_TextField
            id="grossLtvPct"
            label="Gross LTV (Auto)"
            adornment={{ position: "end", value: "%" }}
            defaultValue={formatPercent(details.grossLtvPct)}
            disabled
            readOnly
            variant="outlined"
            size="small"
            margin="dense"
            fullWidth
          />
        </div>

        <div className={classes.frow}>
          <A_TextField
            id="grossLtgdvPct"
            label="Gross LTGDV (Auto)"
            adornment={{ position: "end", value: "%" }}
            defaultValue={formatPercent(details.grossLtgdvPct)}
            disabled
            readOnly
            variant="outlined"
            size="small"
            margin="dense"
            fullWidth
          />
          <A_TextField
            id="netLtvPct"
            label="Net LTV (Auto)"
            adornment={{ position: "end", value: "%" }}
            defaultValue={formatPercent(details.netLtvPct)}
            disabled
            readOnly
            variant="outlined"
            size="small"
            margin="dense"
            fullWidth
          />
        </div>

        <div className={classes.frow} style={{ marginBottom: 0 }}>
          <A_TextField
            id="netLtgdvPct"
            label="Net LTGDV (Auto)"
            adornment={{ position: "end", value: "%" }}
            defaultValue={formatPercent(details.netLtgdvPct)}
            disabled
            readOnly
            variant="outlined"
            size="small"
            margin="dense"
            fullWidth
          />
          <div />
        </div>
      </div>
    </div>
  );
};

ProvisionDetailsSection.propTypes = {
  details: PropTypes.object.isRequired,
  draft: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  editable: PropTypes.bool,
};

export default ProvisionDetailsSection;
